import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { consoleLabel } from '../lib/systems.js';
import { groupGamesByLetter } from '../lib/romUtils.js';
import { GameCard } from './GameCard.jsx';

const SMALL_LIBRARY = 12;

function letterKey(ejsKey, letter) {
  return `${ejsKey}:${letter}`;
}

export function RomLibrary({
  groups,
  favoriteGames = [],
  loading,
  searchTerm,
  onSelectGame,
  onDownloadRom,
  downloadingVaultId,
  onPreviewGame,
  fullPage = false,
}) {
  const [collapsedConsoles, setCollapsedConsoles] = useState(() => new Set());
  const [collapsedLetters, setCollapsedLetters] = useState(() => new Set());
  const [favoritesOpen, setFavoritesOpen] = useState(true);
  const initRef = useRef(false);

  const isFiltering = Boolean(searchTerm?.trim());
  const totalGames = useMemo(
    () => groups.reduce((n, g) => n + g.games.length, 0),
    [groups],
  );

  useEffect(() => {
    if (loading || !groups.length) return;

    if (isFiltering) {
      setCollapsedConsoles(new Set());
      setCollapsedLetters(new Set());
      initRef.current = true;
      return;
    }

    if (initRef.current) {
      setCollapsedConsoles(new Set(groups.map((g) => g.ejsKey)));
      setCollapsedLetters(() => {
        const s = new Set();
        for (const g of groups) {
          for (const { letter } of groupGamesByLetter(g.games)) {
            s.add(letterKey(g.ejsKey, letter));
          }
        }
        return s;
      });
      return;
    }

    const expandAll = totalGames <= SMALL_LIBRARY;
    if (expandAll) {
      setCollapsedConsoles(new Set());
      setCollapsedLetters(new Set());
    } else {
      setCollapsedConsoles(new Set(groups.map((g) => g.ejsKey)));
      setCollapsedLetters(() => {
        const s = new Set();
        for (const g of groups) {
          for (const { letter } of groupGamesByLetter(g.games)) {
            s.add(letterKey(g.ejsKey, letter));
          }
        }
        return s;
      });
    }
    initRef.current = true;
  }, [loading, groups, isFiltering, totalGames]);

  const toggleConsole = useCallback((ejsKey) => {
    setCollapsedConsoles((prev) => {
      const next = new Set(prev);
      if (next.has(ejsKey)) next.delete(ejsKey);
      else next.add(ejsKey);
      return next;
    });
  }, []);

  const toggleLetter = useCallback((ejsKey, letter) => {
    const key = letterKey(ejsKey, letter);
    setCollapsedLetters((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  if (loading && !favoriteGames.length) {
    return <p className="rom-library-empty">A carregar biblioteca…</p>;
  }

  if (!groups.length && !favoriteGames.length) {
    return (
      <p className="rom-library-empty">
        {searchTerm
          ? `Nenhum jogo encontrado para «${searchTerm}».`
          : 'Nenhum jogo no catálogo. Corre npm run roms:vimm:catalog com IDs do Vimm.'}
      </p>
    );
  }

  return (
    <div className={`rom-library-sections${fullPage ? ' rom-library-sections--full' : ''}`}>
      {favoriteGames.length > 0 && (
        <section className="rom-library-block rom-library-block--favorites">
          <button
            type="button"
            className="rom-library-heading rom-library-toggle rom-library-heading--favorites"
            onClick={() => setFavoritesOpen((v) => !v)}
            aria-expanded={favoritesOpen}
          >
            <span className={`rom-library-chevron${favoritesOpen ? ' rom-library-chevron--open' : ''}`} aria-hidden="true">
              ▶
            </span>
            Favoritos
            <span className="rom-library-count">{favoriteGames.length}</span>
          </button>
          {favoritesOpen && (
            <div className="rom-library-grid rom-library-grid--favorites">
              {favoriteGames.map((game) => (
                <GameCard
                  key={`fav-${game.id}`}
                  game={game}
                  ejsKey={game._ejsKey}
                  onSelect={onSelectGame}
                  onPreview={onPreviewGame}
                  onDownload={onDownloadRom}
                  downloading={downloadingVaultId === game.vaultId}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {groups.map((group) => {
        const consoleOpen = !collapsedConsoles.has(group.ejsKey);
        const letterGroups = groupGamesByLetter(group.games);

        return (
          <section key={group.ejsKey} className="rom-library-block">
            <button
              type="button"
              className="rom-library-heading rom-library-toggle"
              onClick={() => toggleConsole(group.ejsKey)}
              aria-expanded={consoleOpen}
            >
              <span className={`rom-library-chevron${consoleOpen ? ' rom-library-chevron--open' : ''}`} aria-hidden="true">
                ▶
              </span>
              {consoleLabel(group.ejsKey, group.vimmSystem)}
              <span className="rom-library-count">{group.games.length}</span>
            </button>

            {consoleOpen && (
              <div className="rom-library-console-body">
                {letterGroups.map(({ letter, games }) => {
                  const lk = letterKey(group.ejsKey, letter);
                  const letterOpen = !collapsedLetters.has(lk);

                  return (
                    <div key={lk} className="rom-library-letter-block">
                      <button
                        type="button"
                        className="rom-library-letter rom-library-toggle"
                        onClick={() => toggleLetter(group.ejsKey, letter)}
                        aria-expanded={letterOpen}
                      >
                        <span className={`rom-library-chevron rom-library-chevron--sm${letterOpen ? ' rom-library-chevron--open' : ''}`} aria-hidden="true">
                          ▶
                        </span>
                        <span className="rom-library-letter__label">{letter}</span>
                        <span className="rom-library-count">{games.length}</span>
                      </button>

                      {letterOpen && (
                        <div className="rom-library-grid">
                          {games.map((game) => (
                            <GameCard
                              key={game.id}
                              game={game}
                              ejsKey={group.ejsKey}
                              onSelect={onSelectGame}
                              onPreview={onPreviewGame}
                              onDownload={onDownloadRom}
                              downloading={downloadingVaultId === game.vaultId}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
